<template>
  <div>
    <ecommerce-statistics
      title="Saved Portfolios"
      explanation="A summary of saved portfolios with tools and table listing"
      :data="portfolioSummary"
    />

    <b-row>
      <b-col xl="3">
        <b-row class="match-height">
          <b-col
            xl="12"
            md="12"
          >
            <future-maturities-chart
              :future-maturities-data="{ futurematurities: futureMaturities }"
              :compact="true"
            />
          </b-col>
          <b-col
            xl="12"
            md="4"
          >
            <ecommerce-pie-chart :data="underlyings" />
          </b-col>
          <b-col
            xl="12"
            md="4"
          >
            <ecommerce-pie-chart :data="issuers" />
          </b-col>
          <b-col
            xl="12"
            md="4"
          >
            <ecommerce-pie-chart :data="productTypes" />
          </b-col>
        </b-row>
      </b-col>

      <b-col xl="9">
        <b-card
          no-body
          class="card-company-table"
        >
          <div class="d-flex justify-content-between">
            <div class="font-weight-bold px-2 pt-2">
              {{ tableData.length }} results found
            </div>
            <div class="px-2 pt-2">
              <b-button
                variant="primary"
                class="mr-1"
                disabled
              >
                <feather-icon icon="PlusIcon" />
                Create Portfolio
              </b-button>
              <b-button
                variant="primary"
                disabled
              >
                Upload Spreadsheet
              </b-button>
            </div>
          </div>
          <b-input-group class="p-2 pt-1 input-group-merge">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              placeholder="Search (ID, name or date)"
              disabled
            />
          </b-input-group>
          <b-table
            class="mb-0"
            :fields="tableFields"
            :items="tableData"
            responsive
            hover
          >
            <template #cell(portfolioName)="data">
              <div class="d-flex align-items-center">
                <b-avatar
                  class="mr-1"
                  :variant="data.item.avatarColor"
                >
                  <feather-icon
                    size="18"
                    :icon="data.item.avatarIcon"
                  />
                </b-avatar>
                <span>{{ data.item.portfolioName }}</span>
              </div>
            </template>
            <template #cell(portfolioIdportfolio)>
              <div class="d-flex align-items-center">
                <b-button
                  variant="gradient-warning"
                  class="btn-icon rounded-circle"
                  disabled
                >
                  <feather-icon icon="Edit2Icon" />
                </b-button>
                <b-button
                  variant="danger"
                  class="btn-icon rounded-circle ml-1"
                  disabled
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </div>
            </template>
          </b-table>
          <div class="p-2 d-flex justify-content-between">
            <div class="my-2">
              <span class="pb-1">Rows Per Page</span>
              <b-form-select
                :value="20"
                :options="[5, 10, 15, 20, 50, 100]"
                class="pagination-select"
                disabled
              />
            </div>
            <div class="my-2 d-flex flex-column justify-content-end">
              <b-pagination
                :value="1"
                :total-rows="tableData.length"
                :per-page="20"
                align="right"
                size="sm"
                class="my-0"
                disabled
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BTable, BAvatar, BFormSelect, BPagination,
  BInputGroup, BInputGroupPrepend, BFormInput,
} from 'bootstrap-vue'
import { $themeColors } from '@themeConfig'
import EcommerceStatistics from '@/views/ecommerce/EcommerceStatistics.vue'
import EcommercePieChart from '@/views/ecommerce/EcommercePieChart.vue'
import FutureMaturitiesChart from '@/views/products/portfolio/charts/FutureMaturitiesChart.vue'

export default {
  name: 'MyPortfoliosStatic',
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BTable,
    BAvatar,
    BFormSelect,
    BPagination,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    EcommerceStatistics,
    EcommercePieChart,
    FutureMaturitiesChart,
  },
  data() {
    return {
      portfolioSummary: [
        {
          color: 'light-primary',
          icon: 'TrendingUpIcon',
          title: '4',
          subtitle: 'Portfolios',
          customClass: 'mb-2 mb-xl-0',
        },
        {
          color: 'light-info',
          icon: 'BoxIcon',
          title: '312',
          subtitle: 'Total products',
          customClass: 'mb-2 mb-xl-0',
        },
        {
          color: 'light-success',
          icon: 'DollarSignIcon',
          title: '7.38%',
          subtitle: 'Avg. return (ann.)',
          customClass: 'mb-2 mb-sm-0',
        },
        {
          color: 'light-warning',
          icon: 'CalendarIcon',
          title: '3.1y',
          subtitle: 'Avg. duration',
          customClass: '',
        },
      ],
      futureMaturities: {
        categories: [
          '2026 H1', '2026 H2', '2027 H1', '2027 H2',
          '2028 H1', '2028 H2', '2029 H1', '2029 H2+',
        ],
        series: [
          {
            name: 'Possible',
            data: [42, 38, 31, 27, 18, 12, 8, 5],
          },
          {
            name: 'Final',
            data: [8, 12, 19, 24, 33, 41, 55, 88],
          },
        ],
      },
      underlyings: {
        title: 'Underlyings',
        explanation: 'Proportion of products by underlying',
        labels: ['FTSE 100', 'Euro Stoxx 50', 'S&P 500', 'Other'],
        series: [41, 29, 18, 12],
        colors: [$themeColors.success, '#28c76f66', '#28c76f33', '#28c76f1a'],
      },
      issuers: {
        title: 'Counterparties',
        explanation: 'Proportion of products by counterparty',
        labels: ['Barclays', 'Soc Gen', 'Goldman', 'Other'],
        series: [34, 26, 22, 18],
        colors: [$themeColors.primary, '#1a73e866', '#1a73e833', '#1a73e81a'],
      },
      productTypes: {
        title: 'Product Types',
        explanation: 'Proportion of products by type',
        labels: ['Autocall', 'Reverse Conv.', 'Protected', 'Other'],
        series: [52, 21, 16, 11],
        colors: [$themeColors.warning, '#ff9f4366', '#ff9f4333', '#ff9f431a'],
      },
      tableFields: [
        {
          key: 'portfolioId',
          label: 'ID',
          thStyle: { width: '5%' },
          sortable: true,
        },
        {
          key: 'portfolioName',
          label: 'Portfolio',
          thStyle: { width: '32%' },
          sortable: true,
        },
        {
          key: 'created',
          label: 'Created',
          thStyle: { width: '17%' },
          sortable: true,
        },
        {
          key: 'modified',
          label: 'Modified',
          thStyle: { width: '17%' },
          sortable: true,
        },
        {
          key: 'productCount',
          label: '# Products',
          thStyle: { width: '17%' },
          sortable: true,
        },
        {
          key: 'portfolioIdportfolio',
          label: 'Action',
          thStyle: { width: '10%' },
        },
      ],
      tableData: [
        {
          portfolioId: 1,
          portfolioName: 'Core Income Portfolio',
          avatarColor: 'light-primary',
          avatarIcon: 'BriefcaseIcon',
          created: '14 Jan 2024',
          modified: '02 Mar 2026',
          productCount: 87,
        },
        {
          portfolioId: 2,
          portfolioName: 'FTSE Autocall Selection',
          avatarColor: 'light-success',
          avatarIcon: 'TrendingUpIcon',
          created: '03 Apr 2024',
          modified: '18 Feb 2026',
          productCount: 64,
        },
        {
          portfolioId: 3,
          portfolioName: 'Capital Protected',
          avatarColor: 'light-warning',
          avatarIcon: 'ShieldIcon',
          created: '22 Jul 2024',
          modified: '11 Mar 2026',
          productCount: 101,
        },
        {
          portfolioId: 4,
          portfolioName: 'European Basket',
          avatarColor: 'light-info',
          avatarIcon: 'GlobeIcon',
          created: '09 Nov 2024',
          modified: '05 Mar 2026',
          productCount: 60,
        },
      ],
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/dashboard-ecommerce.scss';
@import '@core/scss/vue/libs/chart-apex.scss';
</style>

<style lang="scss" scoped>
.pagination-select {
  margin-left: 0.5rem;
  width: auto;
}
</style>
